import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import styles from './AvgStudentProgressAtCurrentLevelDrillDown.module.scss';
import { StudentMetricsDashboardFilters } from '@/school-management/types/generate-report';
import { StudentReportItem } from '@/school-management/types/response';
import { StudentsReportSortByQueryParams } from '@/school-management/types/response/student';
import PageLoader from '@/components/school-management/PageLoader/PageLoader';
import StudentMetricsDashboardTools from '@/components/school-management/StudentMetricsDashboard/StudentMetricsDashboardTools/StudentMetricsDashboardTools';
import GenericTable, {
  TableColumn,
} from '@/components/school-management/GenericTable/GenericTable';
import { getStudentsColumnsData, getStudentsDrillDownData } from '../normalizeData';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import processing, { XlsxData, XlsxTable } from '@/school-management/utils/processing';

const emptyData: StudentReportItem[] = [];

const PAGE_SIZE = 50;

interface AvgStudentProgressAtCurrentLevelDrillDownProps {
  filters: StudentMetricsDashboardFilters;
  drillDownType: string;
  toolsContainer?: any;
  isShowLevelHint?: boolean;
}

const AvgStudentProgressAtCurrentLevelDrillDown: React.FC<
  AvgStudentProgressAtCurrentLevelDrillDownProps
> = ({ filters, drillDownType, toolsContainer, isShowLevelHint }) => {
  const { t } = useAdminTranslation();
  const { user } = useAppSelector(authSelector);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [data, setData] = useState<StudentReportItem[]>(emptyData);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showSubTitle, setShowSubTitle] = useState<boolean>(false);

  const getPageQueryParam = useCallback(() => {
    return {
      page: currentPage,
      pageSize: PAGE_SIZE,
      sortBy: sortBy as StudentsReportSortByQueryParams,
      sortDirection: sortDirection,
    };
  }, [currentPage, sortBy, sortDirection]);

  useEffect(() => {
    let isActive: boolean = true;

    const initData = async () => {
      setIsLoading(true);

      let data: StudentReportItem[] = emptyData;
      let totalPages: number = 0;
      let totalCount: number = 0;

      try {
        const schoolId = user?.metadata?.organizationDetails?.organizationId || '';
        const response = await getStudentsDrillDownData(filters, schoolId, getPageQueryParam());

        if (response) {
          data = response.data;
          totalPages = response.paginationData.totalPages;
          totalCount = response.paginationData.totalCount;
        }
      } catch (er) {}

      if (!isActive) {
        return;
      }

      setData(data);
      setTotalPages(totalPages);
      setIsLoading(false);
      setIsFirstLoading(false);
      setTotalCount(totalCount);
    };

    initData();

    return () => {
      isActive = false;
    };
  }, [filters, user, getPageQueryParam]);

  const columns: TableColumn<StudentReportItem>[] = useMemo(() => {
    return getStudentsColumnsData(t, user);
  }, [t, user]);

  const onTableViewDataChange = useCallback((tableViewData: StudentReportItem[]) => {
    setShowSubTitle(!!(tableViewData || []).find(s => s.isStartingLevelChangedByTeacher));
  }, []);

  const tableName = useMemo(() => {
    return t(`studentMetricsDashboard.drillDown.students.title.${drillDownType}`) || '';
  }, [t, drillDownType]);

  const onDownload = useCallback(async () => {
    console.log('Clicked to download student CSV');

    let levelsData: StudentReportItem[] = emptyData;

    try {
      const schoolId = user?.metadata?.organizationDetails?.organizationId || '';
      const pageQueryParam = getPageQueryParam();
      const response = await getStudentsDrillDownData(filters, schoolId, {
        sortBy: pageQueryParam.sortBy,
        sortDirection: pageQueryParam.sortDirection,
      });

      if (response) {
        levelsData = response.data;
      }
    } catch (er) {}

    const xlsxLevelsColumns = columns.filter(c => !!c.xlsxRender);
    const levelsHeaderRow = xlsxLevelsColumns.map(c => c.xlsxLabel || c.label);

    const xlsxData: XlsxData = {
      fileName: tableName,
      tables: [],
    };

    const levelsTable: XlsxTable = {
      name: tableName,
      headerRow: levelsHeaderRow,
      data: [],
    };
    levelsData.forEach(l => {
      const row: any = {};
      xlsxLevelsColumns.forEach(col => {
        const formattedValue = col.xlsxRender?.((l as any)[col.key], l);
        row[col.xlsxLabel || col.label] = formattedValue;
      });
      levelsTable.data.push(row);
    });
    xlsxData.tables.push(levelsTable);

    processing.createXlsx(xlsxData);
  }, [filters, user, getPageQueryParam, columns, tableName]);

  if (isFirstLoading) {
    return <PageLoader title={t('studentMetricsDashboard.drillDown.students.loadingData') || ''} />;
  }

  return (
    <div className={styles.levelsMode}>
      <StudentMetricsDashboardTools
        title={
          t('studentMetricsDashboard.drillDown.students.totalStudents', {
            totalStudents: totalCount,
          }) || ''
        }
        toolsContainer={toolsContainer}
        totalStudents={totalCount}
      />
      <div className={styles.levelsDetailsCard}>
        <GenericTable
          levelHint={t('studentMetricsDashboard.drillDown.students.allLevelsHint') || ''}
          isShowLevelHint={isShowLevelHint}
          columns={columns}
          data={data}
          rowKey={s => s.userId}
          variant='compact'
          pageSize={PAGE_SIZE}
          disablePagination={false}
          showSortArrows={true}
          title={tableName}
          subTitle={
            showSubTitle ? t('studentMetricsDashboard.drillDown.students.subTitle') || '' : ''
          }
          onTableViewDataChange={onTableViewDataChange}
          onDownload={onDownload}
          serverSideDownload={true}
          downloadFileName={tableName}
          serverSidePagination={true}
          totalPages={totalPages}
          totalCount={totalCount}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          serverSideSorting={true}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={(newSortBy: string | null, newSortDirection: 'asc' | 'desc' | null) => {
            if (newSortBy && newSortDirection) {
              setSortBy(newSortBy);
              setSortDirection(newSortDirection);
              setCurrentPage(1);
            }
          }}
          showLoader={isLoading}
        />
      </div>
    </div>
  );
};

export default AvgStudentProgressAtCurrentLevelDrillDown;
